import type React from 'react';

import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';

import { DeleteVideo, LikeVideo, ReportVideo, VideoPlayer } from 'src/app/shared/components/petitionVideo';

import type { SupporterVideo } from '../../types';
import { PlayPauseIcons } from '../PlayPauseIcons';

import { useVideoComponent } from './hooks/useVideoComponent';

type VideoComponentProps = {
	video: SupporterVideo;
	currentlyPlaying: string;
	setCurrentlyPlaying: (videoUrl: string) => void;
	removeVideoFromState: (videoId?: string) => void;
};

export const VideoComponent: React.FC<VideoComponentProps> = ({
	video,
	currentlyPlaying,
	setCurrentlyPlaying,
	removeVideoFromState,
}) => {
	const { id: videoId, videoUrl, user } = video;

	const {
		data: { isPlaying, videoRef, petitionId, petitionSlug },
		actions: { toggleVideoPlayback, trackVideoError, setIsPlaying },
	} = useVideoComponent({ video, currentlyPlaying, setCurrentlyPlaying });

	return (
		<Flex
			key={videoId}
			sx={{
				flexDirection: 'column',
				// Our theme passes `min-width: 0` by default, but here we want these elements to trigger
				// scrolling rather than shrink.
				minWidth: 'auto',
			}}
			onFocus={(event) => {
				event.target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
			}}
		>
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					height: '200px',
				}}
				data-testid={`video-${videoId}-with-icon`}
			>
				{/* PlayPauseIcons is client-only because it checks the user-agent, which is not available in SSR */}
				<ClientRender>
					<PlayPauseIcons isPlaying={isPlaying} />
				</ClientRender>
				<VideoPlayer
					creator={user?.shortDisplayName}
					ref={videoRef}
					tabIndex={0}
					videoUrl={videoUrl}
					videoId={videoId}
					onClick={() => {
						void toggleVideoPlayback();
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							void toggleVideoPlayback();
						}
					}}
					onPause={(_) => {
						setIsPlaying(false);
					}}
					onError={() => {
						// Unfortunately the event passed to this handler doesn't provide any error details.
						trackVideoError(new Error('Video element error'));
					}}
				/>
			</Box>

			<Flex sx={{ gap: 8 }} my={8}>
				<LikeVideo video={video} petitionId={petitionId} renderedOn="petition_page" />
				<DeleteVideo
					video={video}
					petitionId={petitionId}
					afterDeleteAction={removeVideoFromState}
					renderedOn="petition_page"
				/>
				<ReportVideo video={video} petitionId={petitionId} petitionSlug={petitionSlug} />
			</Flex>
		</Flex>
	);
};
